// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface ScoutTask {
  id: string;
  title: string;
  description?: string;
  priority: 'low' | 'normal' | 'high';
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface NewScoutTask {
  title: string;
  description?: string;
  priority: 'low' | 'normal' | 'high';
}

export const useScoutTasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<ScoutTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchTasks();
    }
  }, [user?.id]);

  const fetchTasks = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('scout_tasks')
        .select('*')
        .eq('created_by', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTasks(data || []);
    } catch (err) {
      console.error('Error fetching scout tasks:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
    } finally {
      setIsLoading(false);
    }
  };

  const createTask = async (taskData: NewScoutTask): Promise<ScoutTask | null> => {
    if (!user?.id) return null;

    try {
      const { data, error } = await supabase
        .from('scout_tasks')
        .insert({
          ...taskData,
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Error creating scout task:', err);
      setError(err instanceof Error ? err.message : 'Failed to create task');
      return null;
    }
  };

  const updateTask = async (taskId: string, updates: Partial<NewScoutTask>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('scout_tasks')
        .update(updates)
        .eq('id', taskId)
        .eq('created_by', user?.id);

      if (error) throw error;

      setTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, ...updates } : task
      ));
      return true;
    } catch (err) {
      console.error('Error updating scout task:', err);
      setError(err instanceof Error ? err.message : 'Failed to update task');
      return false;
    }
  };

  const deleteTask = async (taskId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('scout_tasks')
        .delete()
        .eq('id', taskId)
        .eq('created_by', user?.id);

      if (error) throw error;

      setTasks(prev => prev.filter(task => task.id !== taskId));
      return true;
    } catch (err) {
      console.error('Error deleting scout task:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete task');
      return false;
    }
  };

  return {
    tasks,
    isLoading,
    error,
    createTask,
    updateTask,
    deleteTask,
    refetch: fetchTasks
  };
};
